import { useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import { PermissionCategoryId } from 'modules/CreateWorkspaceModal/types';
import { PermissionId, RoleScope, workspacesConfig } from 'utils/powerLevelsConfig';

import { WorkspaceRolesFormType } from './useWorkspaceRolesForm';
import { cascadePermissionSelections } from './utils';
import { getPermissionCategories } from '../utils';

interface UsePermissionFieldProps {
  actorPowerLevels: { workspace: number; chat: number; meeting: number };
  exclusiveWorkspaceCap?: boolean;
}

interface UsePermissionFieldReturn {
  permissions: Record<PermissionId, boolean>;
  togglePermission: (permissionId: PermissionId) => void;
  toggleAllCategoryPermissions: (categoryId: PermissionCategoryId) => void;
  isCategoryFullySelected: (categoryId: PermissionCategoryId) => boolean;
  isCategoryPartiallySelected: (categoryId: PermissionCategoryId) => boolean;
}

type ScopeSelectionMemory = {
  currentPeak: PermissionId | null;
  /** Up to two previous peaks, oldest first — used when unchecking clears the scope. */
  fallbacks: PermissionId[];
};

const createEmptyScopeMemory = (): ScopeSelectionMemory => ({
  currentPeak: null,
  fallbacks: [],
});

const createEmptySelectionMemory = (): Record<RoleScope, ScopeSelectionMemory> => ({
  workspace: createEmptyScopeMemory(),
  chat: createEmptyScopeMemory(),
  meeting: createEmptyScopeMemory(),
});

const rememberPermissionSelection = (memory: ScopeSelectionMemory, permissionId: PermissionId) => {
  if (memory.currentPeak && memory.currentPeak !== permissionId) {
    memory.fallbacks = [...memory.fallbacks, memory.currentPeak].slice(-2);
  }
  memory.currentPeak = permissionId;
};

const restorePreviousPermissionSelection = (
  permissions: Record<PermissionId, boolean>,
  memory: ScopeSelectionMemory
) => {
  const fallbackPeak = memory.fallbacks.pop() ?? null;

  if (fallbackPeak) {
    memory.currentPeak = fallbackPeak;
    cascadePermissionSelections(permissions, fallbackPeak, true);
    return;
  }

  memory.currentPeak = null;
};

const usePermissionField = ({
  actorPowerLevels,
  exclusiveWorkspaceCap = false,
}: UsePermissionFieldProps): UsePermissionFieldReturn => {
  const { setValue, watch } = useFormContext<WorkspaceRolesFormType>();
  const selectionMemoryRef = useRef(createEmptySelectionMemory());

  const permissions = watch('permissions');
  const permissionCategories = getPermissionCategories({ actorPowerLevels, exclusiveWorkspaceCap });

  const togglePermission = (permissionId: PermissionId) => {
    const scope = workspacesConfig[permissionId]?.scope;
    if (!scope) return;

    const scopeMemory = selectionMemoryRef.current[scope];
    const isEnabling = !permissions[permissionId];
    const newPermissions: Record<PermissionId, boolean> = { ...permissions, [permissionId]: isEnabling };

    if (isEnabling) {
      rememberPermissionSelection(scopeMemory, permissionId);
      cascadePermissionSelections(newPermissions, permissionId, true);
    } else {
      cascadePermissionSelections(newPermissions, permissionId, false);
      restorePreviousPermissionSelection(newPermissions, scopeMemory);
    }

    setValue('permissions', newPermissions, { shouldValidate: true, shouldDirty: true });
  };

  const toggleAllCategoryPermissions = (categoryId: PermissionCategoryId) => {
    const category = permissionCategories.find((c) => c.id === categoryId);
    if (!category) return;

    const isAllSelected = category.permissions.every((permission) => permissions[permission.id]);
    const enableAll = !isAllSelected;
    const newPermissions: Record<PermissionId, boolean> = { ...permissions };

    category.permissions.forEach((permission) => {
      newPermissions[permission.id] = enableAll;
    });

    if (enableAll) {
      const sortedByPl = [...category.permissions].sort(
        (a, b) => workspacesConfig[b.id].powerLevel - workspacesConfig[a.id].powerLevel
      );
      const highestPermission = sortedByPl[0];

      selectionMemoryRef.current[categoryId] = {
        currentPeak: highestPermission?.id ?? null,
        fallbacks: [],
      };

      sortedByPl.forEach((permission) => {
        if (newPermissions[permission.id]) {
          cascadePermissionSelections(newPermissions, permission.id, true);
        }
      });
    } else {
      selectionMemoryRef.current[categoryId] = createEmptyScopeMemory();
      const firstPermission = category.permissions[0];
      if (firstPermission) {
        cascadePermissionSelections(newPermissions, firstPermission.id, false);
      }
    }

    setValue('permissions', newPermissions, { shouldValidate: true, shouldDirty: true });
  };

  const isCategoryFullySelected = (categoryId: PermissionCategoryId) => {
    const category = permissionCategories.find((c) => c.id === categoryId);
    if (!category) return false;

    return category.permissions.every((permission) => permissions[permission.id]);
  };

  const isCategoryPartiallySelected = (categoryId: PermissionCategoryId) => {
    const category = permissionCategories.find((c) => c.id === categoryId);
    if (!category) return false;

    const selectedCount = category.permissions.filter((permission) => permissions[permission.id]).length;
    return selectedCount > 0 && selectedCount < category.permissions.length;
  };

  return {
    permissions,
    togglePermission,
    toggleAllCategoryPermissions,
    isCategoryFullySelected,
    isCategoryPartiallySelected,
  };
};

export default usePermissionField;
